import { useState, useEffect, useRef } from 'react';
import { assign, isEmpty } from 'lodash';
import moment from 'moment';

const initialDateField = {
  value: undefined,
  valid: true,
  invalid: false,
  touched: false,
  pristine: true,
  dirty: false,
  visited: false,
  error: '',
};

function resolveDateRangeChange(startDateField, endDateField) {
  const startDateEmpty = isEmpty(startDateField.value);
  const endDateEmpty = isEmpty(endDateField.value);

  const bothFieldsEmpty = startDateEmpty && endDateEmpty;
  const bothFieldsNotEmpty = !startDateEmpty && !endDateEmpty;
  const bothFieldsValid = startDateField.valid && endDateField.valid;

  if (bothFieldsEmpty) {
    return { startDate: null, endDate: null };
  }

  if (bothFieldsNotEmpty && bothFieldsValid) {
    return {
      startDate: moment.unix(startDateField.value).utc().format('YYYY-MM-DD'),
      endDate: moment.unix(endDateField.value).utc().format('YYYY-MM-DD'),
    };
  }

  return null;
}

export default function useDateRangeFilter(dateRangeFilterPresent, filterDurationRef, onFiltersChange) {
  const dateRangeChanged = useRef(false);

  const [startDateField, setStartDateField] = useState(initialDateField);
  const [endDateField, setEndDateField] = useState(initialDateField);

  useEffect(() => {
    if (dateRangeFilterPresent && dateRangeChanged.current) {
      const change = resolveDateRangeChange(startDateField, endDateField);
      if (change) {
        onFiltersChange('dates', change);
      }
    }
  }, [startDateField, endDateField]);

  function validateDate(value, isStartDate) {
    const error = filterDurationRef.current.validate(value);
    if (isStartDate) {
      setStartDateField(assign({}, startDateField, {
        error,
        value,
        invalid: !isEmpty(error),
        valid: isEmpty(error),
        touched: true,
      }));
    } else {
      setEndDateField(assign({}, endDateField, {
        error,
        value,
        invalid: !isEmpty(error),
        valid: isEmpty(error),
        touched: true,
      }));
    }
  }

  const startCallbacks = ({
    onChange: (value) => {
      dateRangeChanged.current = true;
      setStartDateField(assign({}, startDateField, { value, dirty: true, pristine: false }));
      validateDate(value, true);
    },
    onBlur: () => {
      setStartDateField(assign({}, startDateField, { touched: true, visited: true }));
    },
  });

  const endCallbacks = ({
    onChange: (value) => {
      dateRangeChanged.current = true;
      setEndDateField(assign({}, endDateField, { value, dirty: true, pristine: false }));
      validateDate(value, false);
    },
    onBlur: () => {
      setEndDateField(assign({}, endDateField, { touched: true, visited: true }));
    },
  });

  return {
    startDateField,
    endDateField,
    startCallbacks,
    endCallbacks,
  };
}
